import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import App from './App';
import { msalConfig } from './config/authConfig';
import './styles/index.css';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Handle redirect promise when app loads
// This ensures redirect callbacks from Microsoft authentication are properly handled
console.log('Starting handleRedirectPromise to process Microsoft authentication callbacks');
msalInstance.handleRedirectPromise()
  .then(response => {
    if (response) {
      console.log('Successfully handled redirect response', {
        account: response.account?.username,
        scopes: response.scopes,
        tokenType: response.tokenType,
        hasToken: !!response.accessToken,
        idTokenClaims: response.idTokenClaims ? 'present' : 'missing'
      });
      
      if (response.account) {
        console.log('Setting active account from redirect response');
        msalInstance.setActiveAccount(response.account);
        
        // Store authentication data to prevent redirect loops
        sessionStorage.setItem('msalAuthenticated', 'true');
      }
      
      // Don't use window.location.href for redirect as it causes page reload and can lead to loops
      // Instead, let React Router handle navigation in the App component
      console.log('Authentication successful, UI will redirect via React Router');
    } else {
      console.log('No redirect response detected, normal application start');
      
      // Check if we already have accounts even though we didn't just handle a redirect
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.log('Found existing accounts on startup:', accounts.map(a => a.username));
        msalInstance.setActiveAccount(accounts[0]);
        sessionStorage.setItem('msalAuthenticated', 'true');
      }
    }
  })
  .catch(error => {
    console.error('Error handling redirect:', error);
    // Store error to show to user
    sessionStorage.setItem('msalAuthError', JSON.stringify({
      errorMessage: error.message,
      errorCode: error.errorCode || 'unknown',
      timestamp: new Date().toISOString()
    }));
  });

// Add event listeners for auth events
msalInstance.addEventCallback(event => {
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    console.log('Login success event received');
  } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
    console.log('Logout success event received');
  } else if (event.eventType === EventType.LOGIN_FAILURE) {
    console.error('Login failure event:', event.error);
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <Router>
        <App />
      </Router>
    </MsalProvider>
  </React.StrictMode>
);
